import React from "react";
import { SeoHead } from "@/components/seo/SeoHead";
import { PageContainer } from "@/components/ui/PageContainer";
import { ContentLayout } from "@/components/content/ContentLayout";
import { ContentTable } from "@/components/content/ContentTable";
import { MobileViewportWarning } from "@/components/ui/MobileViewportWarning";
import { Button } from "@/components/ui/button";
import { Sliders, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import "@/styles/print/article-print.css";

const ProvincialRegulatorsGlossary: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Canadian Provincial Securities Regulators"
        description="Directory of Canada's 13 provincial and territorial securities regulators with registration verification links."
        canonicalPath="/glossary/provincial-regulators"
      />

      <header className="border-b border-border/60 bg-slate-900 text-slate-50">
        <PageContainer center>
          <div className="py-8 md:py-10">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Canadian Provincial Securities Regulators</h1>
            <p className="mt-2 text-slate-300 text-base md:text-lg">
              Directory of Canada's 13 provincial and territorial securities regulators with registration verification links.
            </p>
          </div>
        </PageContainer>
      </header>

      <ContentLayout>
        <TooltipProvider>
        <section className="prose prose-slate max-w-none py-8 prose-p:leading-7 prose-p:text-[15px]">
          <h2>Overview</h2>
          <p>
            All investment platforms operating in Canada – including <Link to="/content/can/marketplace-guide" className="underline">alternative investment marketplaces</Link>, exempt market
            dealers (EMDs), and traditional brokerages – operate under the oversight of provincial and territorial
            securities regulators. Before investing through any platform, you should verify their registration status and
            review regulatory guidance on exempt market investments.
          </p>
          <p>
            Canada's securities regulation is decentralized, meaning each province and territory maintains its own
            securities commission with jurisdiction over financial activities within its borders. While the Canadian
            Securities Administrators (CSA) coordinates national policy, registration requirements and investor
            protections can vary by jurisdiction.
          </p>
          <p>
            <strong>Why this matters:</strong> Registration requirements exist to protect investors through
            {" "}
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="underline decoration-dotted cursor-help">KYC</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="text-base leading-tight mb-1"><strong>Know Your Client</strong></div>
                <div>Regulator-mandated process to understand identity, financial situation, and objectives.</div>
              </TooltipContent>
            </Tooltip>
            {" / "}
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="underline decoration-dotted cursor-help">KYP</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="text-base leading-tight mb-1"><strong>Know Your Product</strong></div>
                <div>Assessing investment characteristics, risks, costs, and suitability for the client.</div>
              </TooltipContent>
            </Tooltip>
            {" "}and suitability assessments. Always verify platform registration before transferring funds.
          </p>

          <hr className="my-8 border-border/60" />

          <h2>Provincial &amp; Territorial Regulators</h2>

          <MobileViewportWarning
            threshold={640}
            title="Wide Table"
            message="This table is wider than your screen. Swipe/scroll horizontally to view all columns."
            className="not-prose mb-3"
          >
            <ContentTable tableClassName="glossary-table">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left p-2 border font-semibold">Jurisdiction</th>
                  <th className="text-left p-2 border font-semibold">Regulator (abbr)</th>
                  <th className="text-left p-2 border font-semibold">Website</th>
                  <th className="text-left p-2 border font-semibold hidden sm:table-cell">Check Registration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-medium">British Columbia</td>
                  <td className="p-2 border">British Columbia Securities Commission (BCSC)</td>
                  <td className="p-2 border"><a href="https://www.bcsc.bc.ca" target="_blank" rel="noopener noreferrer">bcsc.bc.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://www.bcsc.bc.ca/industry/registrant-regulation/registration-basics/checking-registration" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Alberta</td>
                  <td className="p-2 border">Alberta Securities Commission (ASC)</td>
                  <td className="p-2 border"><a href="https://www.asc.ca" target="_blank" rel="noopener noreferrer">asc.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://www.checkfirst.ca/check-registration/" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Saskatchewan</td>
                  <td className="p-2 border">Financial and Consumer Affairs Authority (FCAA)</td>
                  <td className="p-2 border"><a href="https://fcaa.gov.sk.ca" target="_blank" rel="noopener noreferrer">fcaa.gov.sk.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://www.fcaa.gov.sk.ca/regulated-businesses-persons/businesses/securities-industry-participants/industry-search" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Manitoba</td>
                  <td className="p-2 border">Manitoba Securities Commission (MSC)</td>
                  <td className="p-2 border"><a href="https://mbsecurities.ca" target="_blank" rel="noopener noreferrer">mbsecurities.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://mbsecurities.ca/registration/index.html" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Ontario</td>
                  <td className="p-2 border">Ontario Securities Commission (OSC)</td>
                  <td className="p-2 border"><a href="https://www.osc.ca" target="_blank" rel="noopener noreferrer">osc.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://www.osc.ca/en/industry/registration-and-compliance/registrant-search" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Quebec</td>
                  <td className="p-2 border">AutoritÃ© des marchÃ©s financiers (AMF)</td>
                  <td className="p-2 border"><a href="https://lautorite.qc.ca" target="_blank" rel="noopener noreferrer">lautorite.qc.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://lautorite.qc.ca/en/general-public/registers/register-of-firms-and-individuals-authorized-to-practice" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">New Brunswick</td>
                  <td className="p-2 border">Financial and Consumer Services Commission (FCNB)</td>
                  <td className="p-2 border"><a href="https://fcnb.ca" target="_blank" rel="noopener noreferrer">fcnb.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://fcnb.ca/en/securities/registrants" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Nova Scotia</td>
                  <td className="p-2 border">Nova Scotia Securities Commission (NSSC)</td>
                  <td className="p-2 border"><a href="https://nssc.novascotia.ca" target="_blank" rel="noopener noreferrer">nssc.novascotia.ca</a></td>
                  <td className="p-2 border hidden sm:table-cell"><a href="https://nssc.novascotia.ca/for-investors" target="_blank" rel="noopener noreferrer">Lookup</a></td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Prince Edward Island</td>
                  <td className="p-2 border">Office of the Superintendent of Securities (PEI OSS)</td>
                  <td className="p-2 border"><a href="https://princeedwardisland.ca/superintendent-securities" target="_blank" rel="noopener noreferrer">princeedwardisland.ca/superintendent-securities</a></td>
                  <td className="p-2 border hidden sm:table-cell"> – </td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Newfoundland and Labrador</td>
                  <td className="p-2 border">Office of the Superintendent of Securities (NL OSS)</td>
                  <td className="p-2 border"><a href="https://www.gov.nl.ca/dgsnl/securities" target="_blank" rel="noopener noreferrer">gov.nl.ca/dgsnl/securities</a></td>
                  <td className="p-2 border hidden sm:table-cell"> – </td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Yukon</td>
                  <td className="p-2 border">Yukon Superintendent of Securities (YT SS)</td>
                  <td className="p-2 border"><a href="https://yukon.ca/en/doing-business/corporate-and-securities-matters" target="_blank" rel="noopener noreferrer">yukon.ca/en/doing-business/corporate-and-securities-matters</a></td>
                  <td className="p-2 border hidden sm:table-cell"> – </td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Northwest Territories &amp; Nunavut</td>
                  <td className="p-2 border">Office of the Superintendent of Securities (OSS)</td>
                  <td className="p-2 border"><a href="https://www.justice.gov.nt.ca/en/divisions/legal-registries-division/securities-office" target="_blank" rel="noopener noreferrer">justice.gov.nt.ca/en/divisions/legal-registries-division/securities-office</a></td>
                  <td className="p-2 border hidden sm:table-cell"> – </td>
                </tr>
              </tbody>
            </ContentTable>
          </MobileViewportWarning>

          <hr className="my-8 border-border/60" />

          <h2>National Resources</h2>

          <h3>Canadian Securities Administrators (CSA)</h3>
          <p>
            The CSA is an umbrella organization of Canada's 13 provincial and territorial securities regulators. While
            each jurisdiction maintains independent authority, the CSA coordinates policy development and harmonizes
            regulation through national instruments like NI 45-106 (Prospectus Exemptions) and NI 45-110 (Start-up
            Crowdfunding Registration).
          </p>
          <p>
            <Globe className="inline-block h-3.5 w-3.5 mr-1 text-muted-foreground" aria-hidden="true" />
            <a href="https://www.securities-administrators.ca" target="_blank" rel="noopener noreferrer">securities-administrators.ca</a>
          </p>

          <h3>Canadian Investor Protection Fund (CIPF)</h3>
          <p>
            CIPF protects eligible customers if an IIROC-registered investment dealer becomes insolvent. Important:
            Exempt Market Dealers (EMDs) are not covered by CIPF. This is why verifying platform registration and
            conducting thorough due diligence is critical when investing through alternative investment marketplaces.
          </p>
          <p>
            <Globe className="inline-block h-3.5 w-3.5 mr-1 text-muted-foreground" aria-hidden="true" />
            <a href="https://www.cipf.ca" target="_blank" rel="noopener noreferrer">cipf.ca</a>
          </p>

          <hr className="my-8 border-border/60" />

          <h2>Build Your Complete Investment Strategy</h2>
          <p>
            Regulatory oversight is a critical foundation for evaluating any investment platform. Do-it-yourself (DIY)
            investing doesn't mean doing-it-alone. Explore the complete Modern Wealth Playbook for guidance on building
            diversified portfolios, understanding risk, and accessing private market opportunities through multiple channels.
          </p>
          <ul>
            <li><strong><a href="/content/can/modern-wealth-playbook">Modern Wealth Playbook →</a></strong></li>
            <li><strong><a href="/content/can/marketplace-guide">Alternative Investment Marketplaces →</a></strong></li>
          </ul>

          <div className="not-prose mt-6 md:mt-8 rounded-xl border border-dashed hover:border-solid border-muted-foreground/30 bg-gradient-to-br from-white to-muted/20 p-4 shadow-sm transition-shadow hover:shadow-xl">
            <div className="grid md:grid-cols-[1fr_auto] items-start gap-4">
              <div className="flex items-start gap-3 text-sm text-foreground">
                <Sliders className="h-6 w-6 md:h-7 md:w-7 shrink-0 text-muted-foreground opacity-30" aria-hidden="true" />
                <div>
                  <div className="font-medium">Build your custom allocation.</div>
                  <div className="text-muted-foreground">Use WealthKit to simulate different portfolio allocations tailored to your risk tolerance and goals.</div>
                </div>
              </div>
              <Link to="/portfolio/tools" className="justify-self-end self-end">
                <Button className="rounded-md bg-[#2D4C7F] hover:bg-[#243a66] text-white">Try WealthKit</Button>
              </Link>
            </div>
          </div>
        </section>
        <div className="not-prose text-[13px] text-muted-foreground mt-12 md:mt-16 mb-10">
          Educational content. Not individualized financial advice. We mention specific platforms for illustration purposes and receive no compensation for these references.
        </div>
        <div className="h-4" aria-hidden="true" />
        </TooltipProvider>
      </ContentLayout>
    </div>
  );
};

export default ProvincialRegulatorsGlossary;
